import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  AttachMoney as PricingIcon,
  Campaign as MarketingIcon,
  Notifications as NotificationsIcon,
  Hub as IntegrationIcon,
  Assessment as ReportingIcon,
  Backup as BackupIcon,
  Description as DocumentationIcon,
  People as StaffIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  SmartToy as AssistantIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import BasicSettings from './BasicSettings';
import FeatureSettings from './FeatureSettings';
import PricingSettings from './PricingSettings';
import MarketingSettings from './MarketingSettings';
import SecuritySettings from './SecuritySettings';
import NotificationSettings from './NotificationSettings';
import AnalyticsSettings from './AnalyticsSettings';
import IntegrationSettings from './IntegrationSettings';
import AdvancedReportingSettings from './AdvancedReportingSettings';
import BackupSettings from './BackupSettings';
import DocumentationSettings from './DocumentationSettings';
import StaffManagementSettings from './StaffManagementSettings';
import InventoryManagementSettings from './InventoryManagementSettings';
import PaymentSettings from './PaymentSettings';
import AssistantSettings from './AssistantSettings';

interface SettingsDashboardProps {
  salonId: string;
  userRole: string;
}

const SettingsDashboard: React.FC<SettingsDashboardProps> = ({ salonId, userRole }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { checkAccess, getRestrictions, loading } = useSettings();

  const tabs = [
    {
      label: 'Basic Settings',
      icon: <BusinessIcon />,
      category: 'basic',
      component: <BasicSettings salonId={salonId} />
    },
    {
      label: 'Features',
      icon: <SettingsIcon />,
      category: 'features',
      component: <FeatureSettings salonId={salonId} />
    },
    {
      label: 'Pricing',
      icon: <PricingIcon />,
      category: 'pricing',
      component: <PricingSettings salonId={salonId} />
    },
    {
      label: 'Marketing',
      icon: <MarketingIcon />,
      category: 'marketing',
      component: <MarketingSettings salonId={salonId} />
    },
    {
      label: 'Security',
      icon: <SecurityIcon />,
      category: 'security',
      component: <SecuritySettings salonId={salonId} />
    },
    {
      label: 'Notifications',
      icon: <NotificationsIcon />,
      category: 'notifications',
      component: <NotificationSettings salonId={salonId} />
    },
    {
      label: 'AI Assistant',
      icon: <AssistantIcon />,
      category: 'assistant',
      component: <AssistantSettings salonId={salonId} />
    },
    {
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      category: 'analytics',
      component: <AnalyticsSettings salonId={salonId} />
    },
    // Super Admin Only Settings
    {
      label: 'Integrations',
      icon: <IntegrationIcon />,
      category: 'integrations',
      component: <IntegrationSettings salonId={salonId} />,
      superAdminOnly: true
    },
    {
      label: 'Advanced Reports',
      icon: <ReportingIcon />,
      category: 'advanced_reporting',
      component: <AdvancedReportingSettings salonId={salonId} />,
      superAdminOnly: true
    },
    {
      label: 'Backup & Recovery',
      icon: <BackupIcon />,
      category: 'backup',
      component: <BackupSettings salonId={salonId} />,
      superAdminOnly: true
    },
    {
      label: 'Documentation',
      icon: <DocumentationIcon />,
      category: 'documentation',
      component: <DocumentationSettings salonId={salonId} />,
      superAdminOnly: true
    },
    {
      label: 'Staff Management',
      icon: <StaffIcon />,
      category: 'staff_management',
      component: <StaffManagementSettings salonId={salonId} />
    },
    {
      label: 'Inventory',
      icon: <InventoryIcon />,
      category: 'inventory',
      component: <InventoryManagementSettings salonId={salonId} />
    },
    {
      label: 'Payment Gateway',
      icon: <PaymentIcon />,
      category: 'payment',
      component: <PaymentSettings salonId={salonId} />,
      superAdminOnly: true
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const availableTabs = tabs.filter(tab => {
    // Check if user has read access to the category
    const hasAccess = checkAccess(tab.category, 'read');
    
    // If it's a Super Admin-only setting, check if user is Super Admin
    if (tab.superAdminOnly) {
      return hasAccess && userRole === 'SUPER_ADMIN';
    }
    
    return hasAccess;
  });

  if (availableTabs.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have access to any settings. Contact your administrator for assistance.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pr: 2, width: '100%', maxWidth: 'none', overflow: 'auto', height: 'calc(100vh - 100px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Settings
          </Typography>
          {userRole === 'SUPER_ADMIN' && (
            <Typography variant="body2" color="secondary" sx={{ mt: -1 }}>
              🔒 Super Admin Access - Integration & Developer options available
            </Typography>
          )}
        </Box>
        <Chip 
          label={userRole.replace('_', ' ')} 
          color={userRole === 'SUPER_ADMIN' ? 'secondary' : 'primary'}
          size="small"
        />
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { 
                  opacity: 0.3 
                },
                width: 48,
                flexShrink: 0,
              },
              '& .MuiTabs-scroller': {
                // Hide scrollbar but allow scrolling
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              },
              '& .MuiTab-root': {
                minWidth: 140,
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontSize: '0.875rem',
                padding: '12px 16px',
                flexShrink: 0,
              },
            }}
          >
            {availableTabs.map((tab) => {
              const restrictions = getRestrictions(tab.category);
              const hasRestrictions = restrictions.length > 0;
              const canWrite = checkAccess(tab.category, 'write');
              
              return (
                <Tab
                  key={tab.category}
                  icon={tab.icon}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.label}
                      {tab.superAdminOnly && (
                        <Chip 
                          label="🔒 Super Admin" 
                          color="secondary" 
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      )}
                      {hasRestrictions && (
                        <Chip 
                          label={restrictions.length} 
                          color="warning" 
                          size="small"
                          title={`${restrictions.length} restrictions apply`}
                        />
                      )}
                      {!canWrite && (
                        <Chip 
                          label="Read Only" 
                          color="info" 
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  iconPosition="start"
                  sx={{
                    ...(tab.superAdminOnly && {
                      backgroundColor: 'rgba(156, 39, 176, 0.08)',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(156, 39, 176, 0.16)',
                      }
                    })
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box sx={{ p: 3, pr: 2 }}>
          {availableTabs[activeTab] && availableTabs[activeTab].component}
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsDashboard;